import { balanceCombat } from "./balance-combat.ts";
import { balanceExpedition } from "./balance-expedition.ts";
import { balanceMarket } from "./balance-market.ts";
import { balanceResume } from "./balance-resume.ts";

// Central tuning values for Nexus Forge, per CONTRACTS §4.
//
// Every value here is something a designer or playtester would
// reasonably want to change without touching logic. Each entry carries
// a one-line comment with its units and the subsystem that reads it.
//
// Not in this file (see companion files):
//   - HTTP status codes      -> shared/http-status.ts   (Step 4)
//   - HSV/RGB color math     -> shared/color.ts         (Step 4)
//   - Centering idioms (/ 2) -> inline, literal OK per contracts:allow-literal
//   - ms->s unit conversion  -> inline, literal OK per contracts:allow-literal

export const balance = {
  movement: {
    // Top speed of the local player, in pixels per second of wall time.
    // Read by client-side player integrator; server trusts client position
    // in Phase 1 (no validation). Changing here changes how "fast" the
    // game feels without any other code changes.
    speedPxPerSec: 200,

    // Minimum wall-time gap between accepted move updates, per player.
    // Enforced on BOTH sides: client self-throttles outbound, server
    // drops any move arriving within this window of the prior one.
    // 50 ms -> ~20 Hz. Tighten (lower) for snappier remote feel at
    // higher bandwidth cost; loosen (higher) to reduce broadcast load.
    clientSendIntervalMs: 50,
  },

  world: {
    // Zone rectangle dimensions in world-space pixels. The tilemap is
    // drawn to these bounds and the camera clamps to them.
    widthPx: 1600,
    heightPx: 1600,

    // Tile size in pixels. Tilemap draws widthPx/tileSizePx x
    // heightPx/tileSizePx tiles. Must divide width and height.
    tileSizePx: 32,

    // Position assigned to a player on their very first join, in
    // world-space pixels. PlayerDO writes these into the DB row.
    defaultSpawnX: 100,
    defaultSpawnY: 100,
  },

  player: {
    // On-screen side length of a player sprite, in pixels. Also used
    // by the client-side boundary clamp (half of this is the margin).
    sizePx: 24,
  },

  input: {
    // KeyboardEvent.code for the gather interaction. The client binds
    // keydown / keyup on this code for hold-to-gather. "KeyE" is the
    // physical E key regardless of OS layout; use "code" rather than
    // "key" so AZERTY / Dvorak users press the same physical button.
    gatherKey: "KeyE",

    workbenchKey: "KeyB", // open/close the personal workbench panel
    marketKey: "KeyM",    // open/close the market panel (Phase 4 Step 15)
    statsKey: "KeyK",     // open/close the player-stats panel (Phase 6 Block B)

    // Phase 5 Step 15b — combat + party bindings. Slot1-4 map to the
    // player's 4 active abilities (loadout hardcoded in Phase 5);
    // Space triggers basic-attack. partyKey opens the party panel in
    // Step 16; reserved here so balance-input is stable.
    partyKey: "KeyP",
    abilitySlot1Key: "Digit1",
    abilitySlot2Key: "Digit2",
    abilitySlot3Key: "Digit3",
    abilitySlot4Key: "Digit4",
    basicAttackKey: "Space",
    galaxyMapKey: "KeyG", // open/close the galaxy-map / jump panel (Phase 6)
    inventoryKey: "KeyI", // open/close the full inventory modal (Phase 7 Block C)
    friendsKey: "KeyF",   // open/close the friends list panel (Phase 7 Block F)
  },

  workbench: {
    // Fraction of the viewport that the workbench panel covers (width and height).
    panelWidthRatio: 0.80,
    panelHeightRatio: 0.80,

    // Left column (recipe list) as a fraction of the panel width.
    // Right column (queue) fills the remainder.
    leftColRatio: 0.55,

    // Alpha applied to tier-2 recipe rows that are skill-gated and currently locked.
    lockedRecipeAlpha: 0.70,

    // Alpha applied to the lock-indicator stroke on a locked recipe row.
    lockedStrokeAlpha: 0.50,
  },

  client: {
    // While the workbench panel is open, the client polls getWorkshop on
    // this interval to pick up alarm-driven completions from PlayerDO. The
    // server reply (workshopUpdated) is the same shape that push paths use,
    // so the message handler is the same. Tune up to reduce traffic; tune
    // down to make offline-completed jobs surface faster after open.
    workshopPollIntervalMs: 5_000,
    // Cadence at which the client polls getMarketView while the market
    // panel is open (Phase 4 Step 19). Stops when the panel closes.
    marketPollIntervalMs: 3_000,
  },

  network: {
    // Initial backoff when the client's WebSocket drops. Reset on every
    // successful connect, in milliseconds.
    reconnectInitialMs: 1000,

    // Upper bound on reconnect backoff, in milliseconds. Without this,
    // a long outage would eventually produce very long sleeps between
    // attempts; 30s is a reasonable ceiling for a realtime game.
    reconnectMaxMs: 30_000,

    // Per-attempt multiplier applied to the current backoff delay, so
    // the sequence is 1s, 2s, 4s, ... clamped to reconnectMaxMs.
    reconnectBackoffFactor: 2,

    // Random jitter as a fraction of the current backoff delay. A value
    // of 0.3 means each retry sleeps for delay + (0..30% of delay).
    // Prevents thundering-herd reconnects after a Cloudflare edge
    // hiccup: without jitter, every client retrying in lockstep after
    // a shared failure would slam the same ZoneDO simultaneously.
    // See DECISIONS "Resolved in Phase 2 -- added 30% jitter".
    reconnectJitterRatio: 0.3,
  },

  rendering: {
    // Two alternating tile shades for the checkerboard background, as
    // 24-bit RGB integers. Picked to give subtle motion reference on a
    // dark canvas without dominating over player sprites.
    tileShadeLight: 0x2a2a2a,
    tileShadeDark: 0x242424,

    // Half-size (radius) of the diamond shape drawn for each resource
    // node, in world-space pixels. Diamond = rotated square with each
    // corner nodeSizePx from center.
    nodeSizePx: 10,

    // Alpha applied to a depleted node's graphic so it reads as
    // unavailable without fully vanishing from the world.
    nodeDepletedAlpha: 0.3,

    // Hold-to-gather progress bar dimensions, in screen pixels.
    gatherBarWidthPx: 48,
    gatherBarHeightPx: 4,
    // Distance above the player's center Y where the bar's top sits.
    gatherBarOffsetY: 20,
    // Background and fill tints for the progress bar.
    gatherBarBgColor: 0x444444,
    gatherBarFillColor: 0x66ff66,
  },

  gathering: {
    // Hold time for a single gather, in milliseconds. Progress bar
    // fills over this duration; on completion one unit lands in
    // inventory. Tighter = faster loop but less tension; looser =
    // more committal, more room for interruption to matter.
    gatherDurationMs: 2000,

    // Maximum distance in pixels between the player and the resource
    // node at gather completion time. Server-authoritative: a gather
    // that completes with the player farther than this is rejected
    // with "out_of_range". Sized to comfortably cover the player
    // sprite plus a small slack margin.
    maxRangePx: 64,

    // Number of successful gathers a node yields before depleting.
    // Also the value remaining_yield is reset to when the respawn
    // alarm fires. Raising this makes individual nodes last longer;
    // lowering makes depletion and respawn cycles more visible.
    nodeYield: 5,

    // Time between a node depleting and the respawn alarm restoring
    // remaining_yield, in milliseconds. Tune alongside nodeYield to
    // set the sustainable gather rate across the zone.
    nodeRespawnMs: 30_000,
  },

  inventory: {
    // Total slots per player. Hitting this cap with all slots full
    // of distinct items makes addItem return "inventory_full". A
    // partial stack of a matching itemId still accepts more even
    // when all slots are occupied.
    maxSlots: 30,

    // Maximum count per slot. addItem spills over into new slots when
    // an existing stack reaches this limit. Items.ts entries use this
    // as their default until a per-item override is introduced.
    maxStackSize: 100,

    // Inventory toolbar layout (client-only, read by inventory-ui.ts).
    // Side length of one slot square, in screen pixels.
    slotSizePx: 36,
    // Gap between adjacent slots, in screen pixels.
    slotGapPx: 3,
    // Columns in each row; 2 rows × slotsPerRow = maxSlots.
    slotsPerRow: 15,
    // Opacity of empty slot backgrounds; 0 = invisible, 1 = fully opaque.
    emptySlotAlpha: 0.5,
  },

  skills: {
    // Base XP awarded to the primary skill for one completed gather.
    xpPerGather: 1.0,

    // Fractional multiplier applied to each 1-hop neighbor in the skill
    // web when XP is granted to a primary skill. Phase 6 Block B reduced
    // from 0.25 to 0.1 per design decision — the web has more edges now
    // and full 0.25 adjacent would level adjacent skills too fast.
    adjacentXpRatio: 0.1,

    // XP awarded to each primary skill for one completed craft job.
    xpPerCraft: 5.0,

    // Market Analysis skill XP (Phase 6 Block C). Querying a remote
    // market earns 1 XP; completing a market transaction earns 5 XP.
    marketAnalysisXpPerQuery: 1,
    marketAnalysisXpPerTransaction: 5,

    // Tier threshold bands (inclusive lower bound, exclusive upper).
    // skillTierReached fires whenever accumulated XP crosses one of
    // these boundaries upward. Names are display labels.
    tierThresholds: {
      novice:      { min: 0,    label: "Novice"      },
      apprentice:  { min: 100,  label: "Apprentice"  },
      journeyman:  { min: 500,  label: "Journeyman"  },
      expert:      { min: 1500, label: "Expert"      },
      master:      { min: 4000, label: "Master"      },
    },
  },

  // npc + economy live in shared/balance-market.ts so the market
  // tuning surface can grow without bumping balance.ts past the §10
  // cap. Per DECISIONS 2026-04-18 "Phase 4 Step 18 balance-*.ts
  // sibling-file split". Assignment rather than spread preserves
  // `as const` literal types through the composition.
  npc: balanceMarket.npc,
  economy: balanceMarket.economy,

  // Resume-protocol tuning lives in shared/balance-resume.ts; same
  // sibling-file pattern as balance-market.ts. Phase 5 Step 1.
  resume: balanceResume.resume,

  // Combat tuning lives in shared/balance-combat.ts; same sibling-file
  // pattern. Phase 5 Step 9. Per-ability cooldowns/damage stay in the
  // ability catalog (shared/abilities.ts); this surface carries
  // cross-entity knobs only (hit chance, tick cadence, defaults,
  // durability/repair costs, boss AoE radius).
  combat: balanceCombat.combat,

  // Expedition + party tuning lives in shared/balance-expedition.ts.
  // Phase 5 Step 11. Archetype geometry + per-mob stats stay in data
  // catalogs; this surface is lifecycle + party-protocol knobs only.
  expedition: balanceExpedition.expedition,

  crafting: {
    // Maximum number of craft jobs that can be queued at once per player.
    // A full queue rejects new queueCraft calls with "queue_full".
    maxQueueDepth: 10,

    // Quality roll parameters. Read by shared/quality.ts.
    quality: {
      // Starting center of the quality distribution at 0 XP.
      // A brand-new crafter targets ~40 (lower-Common range).
      baseTargetQuality: 40,

      // Per-XP gain on the target quality center.
      // At 100 XP: target = clamp(40 + 100 * 0.5, 40, 85) = 85.
      skillQualityMultiplier: 0.5,

      // Maximum target quality (plateau). Masterwork is still reachable
      // via variance and jitter even at the cap.
      targetQualityCap: 85,

      // Starting variance (±30) around the target at 0 XP.
      // Controls the width of the triangular distribution.
      baseVariance: 30,

      // Per-XP reduction in variance. At 100 XP: variance = clamp(30 - 100 * 0.2, 8, 30) = 10.
      varianceDecay: 0.2,

      // Minimum variance floor. Keeps rolls non-deterministic even for
      // highly skilled crafters.
      minVariance: 8,

      // Uniform jitter magnitude added to every roll: uniform(-3, +3).
      // Prevents a precise XP total from always landing in the same tier.
      jitterMagnitude: 3,
    },

    // Quality bucket boundaries (inclusive). scoreToTier in quality.ts
    // maps a [0, 100] score to the matching tier name.
    buckets: {
      poor:       { min: 0,  max: 19 },
      common:     { min: 20, max: 49 },
      good:       { min: 50, max: 74 },
      fine:       { min: 75, max: 89 },
      masterwork: { min: 90, max: 100 },
    },

    // Alarm handler tolerance: a job whose completes_at is within this
    // many milliseconds of now() is treated as due. Absorbs minor clock
    // skew between the alarm scheduler and the handler invocation.
    alarmTolerance: 500,

    // Craft time per single output item for each recipe, in milliseconds.
    craftTimesMs: {
      copperWire: 15_000,
      polymerPlate: 20_000,
      basicCircuit: 45_000,
      ferriteCore: 60_000,
      signalAmplifier: 120_000,
      // Phase 6 Block B tier-2 exotic recipes (require new skills + boss-drop inputs)
      quantumFilamentBrace: 180_000,
      plasmaConduit: 180_000,
      reinforcedHullPlate: 150_000,
    },

    // Input material counts > 1 for each recipe.
    // count: 1 inputs use the TypeScript literal 1 (permitted per §4).
    inputCounts: {
      copperWireCopper: 3,         // Copper Wire: 3 copper → 1 wire
      polymerPlatePolymer: 2,      // Polymer Plate: 2 polymer → 1 plate
      basicCircuitCopperWire: 2,   // Basic Circuit: 2 copper_wire + 1 silicon
      ferriteCoreFerite: 3,        // Ferrite Core: 3 ferrite + 1 copper_wire
    },

    // Minimum skill XP required to queue a tier-2 recipe.
    // Checked at queue time AND at alarm-fire time (cheap double-check).
    skillGateXp: {
      basicCircuitElectronics: 50,
      ferriteCrafting: 75,
      signalAmplifierElectronics: 150,
      signalAmplifierCrafting: 150,
      // Phase 6 Block B — new exotic recipes gate at Journeyman (500 XP)
      quantumFilamentBraceCrafting: 500,
      quantumFilamentBraceElectronics: 500,
      plasmaConduitWiring: 500,
      plasmaConduitPowerSystems: 500,
      reinforcedHullPlateCrafting: 500,
      reinforcedHullPlateChemistry: 500,
    },
  },

  // Phase 6 — inter-station jump-gate travel.
  travel: {
    // Credits deducted from the player on jump initiation. Server-side
    // only; client display reads this via the snapshot's balance bundle.
    jumpCreditsCost: 50,

    // Time from jumpStarted to jumpCompleted alarm fire, in milliseconds.
    // Alarm fires regardless of socket state; client shows a travel overlay
    // and reconnects per resume protocol on jumpCompleted arrival.
    jumpDurationMs: 10_000,
  },
} as const;

// Compile-time-ish guard for any balance value that is interpolated
// into a SQL DDL string (e.g. ALTER TABLE … DEFAULT N). SQLite has no
// parameter binding for DEFAULTs, so these values cross the
// SQL-string boundary as raw text. A non-integer would still emit
// valid DDL but break the schema invariant (e.g. an INTEGER NOT NULL
// column with a fractional DEFAULT). Throw at module load — a fail-
// fast on import is cheaper than discovering it on the next DO wake.
//
// Per DECISIONS 2026-04-18 "Phase 4 Step 4 DDL-interpolation guard for
// balance values": every balance value referenced from a DDL string
// goes in this list.
const ddlInterpolatedIntegers: ReadonlyArray<{ name: string; value: number }> = [
  { name: "balance.economy.startingCredits", value: balance.economy.startingCredits },
  { name: "balance.combat.defaultPlayerMaxHp", value: balance.combat.defaultPlayerMaxHp },
];
for (const { name, value } of ddlInterpolatedIntegers) {
  if (!Number.isInteger(value)) {
    throw new Error(
      `balance.ts: ${name} must be an integer (interpolated into DDL); got ${value}`,
    );
  }
}
